<template>
    <div class="ds-employees-filter" v-click-outside="close" @keydown.esc.stop="closeAndFocus">
        <button
            ref="trigger"
            type="button"
            class="ds-employees-control ds-employees-filter-trigger"
            :class="{ 'is-selected': selectedCount > 0, 'is-open': isOpen }"
            aria-haspopup="listbox"
            :aria-expanded="isOpen"
            @click="toggleOpen"
        >
            <span>{{ label }}</span>
            <span v-if="selectedCount" class="ds-employees-filter-count" :aria-label="`выбрано: ${selectedCount}`">{{ selectedCount }}</span>
            <svg class="ds-employees-chevron" :class="{ 'is-open': isOpen }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
        </button>
        <div
            v-if="isOpen"
            class="ds-glass-menu ds-employees-filter-popover"
            :class="{ 'is-align-right': alignRight }"
        >
            <div class="ds-employees-filter-search">
                <label class="ds-employees-filter-search-field">
                    <icon width="14" height="14" vb-size="16" class="ds-employees-search-icon"><IconSearch /></icon>
                    <input
                        ref="searchInput"
                        v-model="searchText"
                        type="text"
                        class="ds-employees-search-input"
                        :placeholder="searchPlaceholder"
                        :aria-label="searchPlaceholder"
                        maxlength="100"
                    >
                    <button v-if="searchText" type="button" class="ds-employees-search-clear" aria-label="Очистить поиск" @click="clearSearch">
                        <icon width="16" height="16" vb-size="24"><IconClose /></icon>
                    </button>
                </label>
            </div>
            <div class="ds-employees-filter-hint">Можно выбрать несколько</div>
            <div
                class="ds-employees-filter-list"
                role="listbox"
                aria-multiselectable="true"
                :aria-label="`Фильтр «${label}»`"
                :style="listMaxHeight ? { maxHeight: `${listMaxHeight}px` } : undefined"
            >
                <div
                    v-for="option in visibleOptions"
                    :key="option.id"
                    role="option"
                    tabindex="0"
                    class="ds-employees-filter-option"
                    :class="{ 'is-selected': isChecked(option.id) }"
                    :aria-selected="isChecked(option.id)"
                    @click="toggle(option.id)"
                    @keydown.enter.prevent="toggle(option.id)"
                    @keydown.space.prevent="toggle(option.id)"
                >
                    <span class="ds-employees-filter-option-label">
                        <template v-for="(part, index) in highlightParts(option.name)" :key="index">
                            <mark v-if="part.match" class="ds-employees-filter-match">{{ part.text }}</mark>
                            <template v-else>{{ part.text }}</template>
                        </template>
                    </span>
                    <span class="ds-employees-filter-check" aria-hidden="true">
                        <svg v-if="isChecked(option.id)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                    </span>
                </div>
                <div v-if="!visibleOptions.length" class="ds-employees-filter-empty">Ничего не нашли</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import IconClose from '@/ui/icons/IconClose.vue'
import IconSearch from '@/ui/icons/IconSearch.vue'
import type { FilterOption } from './employeesList'

interface Props {
    label: string,
    options: FilterOption[],
    searchPlaceholder: string
}

interface HighlightPart {
    text: string,
    match: boolean
}

const { label, options, searchPlaceholder } = defineProps<Props>()

// Выбранные ID; выбор применяется сразу, меню остаётся открытым.
const selected = defineModel<string[]>({ default: () => [] })

// Ориентир — до 12 пунктов (34 + 2 px), дальше прокрутка внутри меню.
const MAX_VISIBLE_OPTIONS = 12
const OPTION_PITCH = 36
const POPOVER_WIDTH = 300
const POPOVER_GAP = 8
const VIEWPORT_MARGIN = 12
// Поиск, подпись и вертикальные поля поповера над списком.
const POPOVER_CHROME_HEIGHT = 80

const isOpen = ref(false)
const searchText = ref('')
const trigger = ref<HTMLButtonElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const alignRight = ref(false)
const listMaxHeight = ref<number | null>(null)

const selectedCount = computed(() => selected.value.length)

// Поиск только сужает список, порядок справочника и выбор не меняются.
const visibleOptions = computed(() => {
    const text = searchText.value.trim().toLowerCase()
    if (!text) return options
    return options.filter(option => option.name.toLowerCase().includes(text))
})

// Подсветка совпадений без v-html: текст выводится как есть.
const highlightParts = (name: string): HighlightPart[] => {
    const query = searchText.value.trim().toLowerCase()
    if (!query) return [{ text: name, match: false }]
    const lower = name.toLowerCase()
    const parts: HighlightPart[] = []
    let position = 0
    let index = lower.indexOf(query)
    while (index !== -1) {
        if (index > position) parts.push({ text: name.slice(position, index), match: false })
        parts.push({ text: name.slice(index, index + query.length), match: true })
        position = index + query.length
        index = lower.indexOf(query, position)
    }
    if (position < name.length) parts.push({ text: name.slice(position), match: false })
    return parts
}

const isChecked = (id: string) => selected.value.includes(id)

const toggle = (id: string) => {
    selected.value = isChecked(id)
        ? selected.value.filter(item => item !== id)
        : [...selected.value, id]
}

const clearSearch = () => {
    searchText.value = ''
    searchInput.value?.focus()
}

// Меню не выходит за границы окна: выравнивание по правому краю trigger и высота списка по доступному месту.
const updatePlacement = () => {
    const rect = trigger.value?.getBoundingClientRect()
    if (!rect) return
    alignRight.value = rect.left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_MARGIN
    const available = window.innerHeight - rect.bottom - POPOVER_GAP - VIEWPORT_MARGIN - POPOVER_CHROME_HEIGHT
    listMaxHeight.value = Math.max(OPTION_PITCH * 2, Math.min(OPTION_PITCH * MAX_VISIBLE_OPTIONS, available))
}

const open = async () => {
    updatePlacement()
    isOpen.value = true
    await nextTick()
    searchInput.value?.focus()
}

const close = () => {
    isOpen.value = false
}

const toggleOpen = () => {
    if (isOpen.value) close()
    else open()
}

const closeAndFocus = () => {
    if (!isOpen.value) return
    close()
    trigger.value?.focus()
}

watch(isOpen, value => {
    if (value) {
        window.addEventListener('resize', updatePlacement)
    } else {
        window.removeEventListener('resize', updatePlacement)
        searchText.value = ''
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updatePlacement)
})
</script>
